import { httpClient } from '@/core/api/httpClient';
import type {
  Member,
  MembersResponse,
  MemberDtoPagedResult,
  UpdateMemberPayload,
  ToggleMemberPayload,
  CreateInvitationPayload,
  AssignMemberAreaPayload,
  SetMemberHireDatePayload,
  InvitationsQueryParams,
  InvitationDtoPagedResult,
} from '../types';
import { adaptMemberDto } from '../adapters/member.adapter';
import { membersMock } from './members.mock';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';
import { roleToApi, type MemberRole } from '@/core/constants/roles';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

// ─── Query params ─────────────────────────────────────────────────────────────

/**
 * Query parameters for GET /api/tenants/members.
 *
 * @remarks
 * Field names use PascalCase (`Page`, `PageSize`, `SearchTerm`) to match the
 * backend's ASP.NET convention. Do NOT rename to camelCase — the server will
 * silently ignore lowercase params and return the first page with no filtering.
 *
 * `Page` is 1-based on the server. `useMembers` converts from the 0-based
 * `pageIndex` used by the UI before building this params object.
 */
export interface MembersQueryParams {
  Page?: number;
  PageSize?: number;
  SearchTerm?: string;
  SortBy?: string;
  SortAscending?: boolean;
}

// ─── Members ──────────────────────────────────────────────────────────────────

export const membersService = {
  /**
   * Returns a paginated list of members for the active tenant.
   * Transforms `MemberDtoPagedResult` into the `MembersResponse` shape
   * expected by `useDataSource`.
   */
  getAll: async (
    params: MembersQueryParams,
    currentUserId?: string,
    signal?: AbortSignal,
  ): Promise<MembersResponse> => {
    if (USE_MOCK) {
      let items = membersMock.map((m) => ({
        ...m,
        isCurrentUser: !!currentUserId && m.userId === currentUserId,
      }));

      if (params.SearchTerm) {
        const term = params.SearchTerm.toLowerCase();
        items = items.filter(
          (m) => m.displayName.toLowerCase().includes(term) || m.email.toLowerCase().includes(term),
        );
      }

      const page = params.Page ?? 1;
      const pageSize = params.PageSize ?? 20;
      const total = items.length;
      const totalPages = Math.ceil(total / pageSize);
      const start = (page - 1) * pageSize;

      return { items: items.slice(start, start + pageSize), total, totalPages };
    }

    const { data } = await httpClient.get<MemberDtoPagedResult>(`${API_V1}/tenants/members`, {
      params,
      signal,
    });

    return {
      items: (data.items ?? []).map((dto) => adaptMemberDto(dto, currentUserId)),
      total: data.totalCount,
      totalPages: data.totalPages,
    };
  },

  /**
   * Toggles the active/inactive status of a member.
   * PATCH /api/tenants/members/{memberId}/toggle
   */
  toggle: async (memberId: string, payload: ToggleMemberPayload): Promise<void> => {
    if (USE_MOCK) {
      const member = membersMock.find((m) => m.id === memberId);
      if (member) member.isActive = payload.activate;
      return;
    }

    await httpClient.patch<ToggleMemberPayload, void>(
      `${API_V1}/tenants/members/${memberId}/toggle`,
      payload,
    );
  },

  /**
   * Removes a member from the tenant.
   * DELETE /api/tenants/members/{memberId}
   */
  remove: async (memberId: string): Promise<void> => {
    if (USE_MOCK) {
      const index = membersMock.findIndex((m) => m.id === memberId);
      if (index !== -1) membersMock.splice(index, 1);
      return;
    }

    await httpClient.delete(`${API_V1}/tenants/members/${memberId}`);
  },

  /**
   * Updates a member's role, expiration or ownership.
   * PATCH /api/tenants/members/{memberId}
   *
   * @remarks
   * This endpoint is NOT used for inline cell editing — a dedicated PATCH
   * endpoint will be added by the backend for that purpose.
   */
  update: async (memberId: string, payload: UpdateMemberPayload): Promise<void> => {
    if (USE_MOCK) {
      const member = membersMock.find((m) => m.id === memberId);
      if (!member) throw new Error('Member not found');
      if (payload.role) member.role = payload.role as Member['role'];
      if (payload.expiresAt !== undefined) member.expiresAt = payload.expiresAt;
      return;
    }

    await httpClient.patch<UpdateMemberPayload, void>(`${API_V1}/tenants/members/${memberId}`, {
      ...payload,
      role: payload.role ? roleToApi(payload.role as MemberRole) : payload.role,
    });
  },

  /**
   * Assigns (or unassigns) a member to an area.
   * PUT /api/tenants/members/{memberId}/area
   *
   * @remarks
   * `areaId` is sent as a string — the backend contract was updated from int64.
   * Pass `areaId = null` to remove the member's current area assignment.
   */
  assignMemberArea: async (memberId: string, areaId: string | null): Promise<void> => {
    if (USE_MOCK) {
      const member = membersMock.find((m) => m.id === memberId);
      if (member) member.areaId = areaId;
      return;
    }

    const payload: AssignMemberAreaPayload = { areaId };
    await httpClient.put<AssignMemberAreaPayload, void>(
      `${API_V1}/tenants/members/${memberId}/area`,
      payload,
    );
  },

  /**
   * Sets a member's hire date — required before TimeOff can open an
   * entitlement cycle for them (`TimeOff.MemberHasNoHireDate` otherwise).
   * PUT /api/tenants/members/{userId}/hire-date
   *
   * @remarks
   * `userId`, not the membership `id` — see `SetMemberHireDatePayload`.
   */
  setHireDate: async (userId: string, payload: SetMemberHireDatePayload): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.put<SetMemberHireDatePayload, void>(
      `${API_V1}/tenants/members/${userId}/hire-date`,
      payload,
    );
  },
};

// ─── Invitations ──────────────────────────────────────────────────────────────

export const invitationsService = {
  /**
   * Creates a new invitation for the active tenant.
   * POST /api/tenants/invitations
   */
  create: async (payload: CreateInvitationPayload): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.post<CreateInvitationPayload, void>(`${API_V1}/tenants/invitations`, {
      ...payload,
      role: roleToApi(payload.role as MemberRole),
    });
  },

  /**
   * Returns paginated invitations for the active tenant.
   * GET /api/tenants/invitations
   */
  getAll: async (
    params?: InvitationsQueryParams,
    signal?: AbortSignal,
  ): Promise<InvitationDtoPagedResult> => {
    if (USE_MOCK) {
      return {
        items: [],
        page: params?.Page ?? 1,
        pageSize: params?.PageSize ?? 20,
        totalCount: 0,
        totalPages: 1,
        hasPreviousPage: false,
        hasNextPage: false,
        firstItemOnPage: 0,
        lastItemOnPage: 0,
      };
    }
    const { data } = await httpClient.get<InvitationDtoPagedResult>(
      `${API_V1}/tenants/invitations`,
      {
        params,
        signal,
      },
    );
    return data;
  },

  /**
   * Revokes an invitation.
   * DELETE /api/tenants/invitations/{invitationId}
   */
  cancel: async (invitationId: string): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.delete(`${API_V1}/tenants/invitations/${invitationId}`);
  },
};
